// components/LoadingSpinner/LoadingSpinner.js
import React, { useState, useEffect } from 'react';
import './LoadingSpinner.css';

const LoadingSpinner = ({ 
  size = 'large', 
  theme = 'modern',
  color = 'primary',
  fullScreen = true,
  overlay = true,
  progress = null,
  animated = true,
  className = ''
}) => {
  const [dots, setDots] = useState('');

  // ✅ FIXED: Only animate dots when necessary
  useEffect(() => {
    if (!animated) return;
    
    const interval = setInterval(() => {
      setDots(prev => prev.length >= 3 ? '' : prev + '.');
    }, 500);

    return () => clearInterval(interval);
  }, [animated]);

  const colors = {
    primary: {
      main: '#3b82f6',
      secondary: '#93c5fd',
      accent: '#1d4ed8',
      gradient: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
      background: 'rgba(59, 130, 246, 0.05)'
    },
    success: {
      main: '#10b981',
      secondary: '#86efac',
      accent: '#059669',
      gradient: 'linear-gradient(135deg, #10b981 0%, #06b6d4 100%)',
      background: 'rgba(16, 185, 129, 0.05)'
    },
    purple: {
      main: '#8b5cf6',
      secondary: '#c4b5fd',
      accent: '#7c3aed',
      gradient: 'linear-gradient(135deg, #8b5cf6 0%, #d946ef 100%)',
      background: 'rgba(139, 92, 246, 0.05)'
    }
  };

  const currentColor = colors[color] || colors.primary;

  const ModernSpinner = () => (
    <div className="modern-spinner-container">
      <div 
        className="modern-spinner"
        style={{
          borderTopColor: currentColor.main,
          borderRightColor: currentColor.secondary,
          boxShadow: `0 8px 32px ${currentColor.main}20`
        }}
      />
      <div 
        className="modern-spinner-inner"
        style={{
          borderTopColor: currentColor.accent,
          borderRightColor: 'transparent'
        }}
      />
    </div>
  );

  const GradientSpinner = () => (
    <div className="gradient-container">
      <div 
        className="gradient-spinner"
        style={{
          background: currentColor.gradient,
          boxShadow: `0 8px 32px ${currentColor.main}30`
        }}
      />
      <div className="gradient-center" />
    </div>
  );

  const PulseSpinner = () => (
    <div className="pulse-container">
      {[1, 2, 3, 4].map(i => (
        <div
          key={i}
          className="pulse-circle"
          style={{
            backgroundColor: currentColor.main,
            animationDelay: `${(i - 1) * 0.15}s`,
            boxShadow: `0 0 20px ${currentColor.main}40`
          }}
        />
      ))}
    </div>
  );

  const renderSpinner = () => {
    switch(theme) {
      case 'gradient': return <GradientSpinner />;
      case 'pulse': return <PulseSpinner />;
      case 'modern':
      default: return <ModernSpinner />;
    }
  };

  const containerClass = `loading-spinner ${fullScreen ? 'full-screen' : ''} ${overlay ? 'with-overlay' : ''} ${className}`;

  return (
    <div className={containerClass}>
      <div className="spinner-content">
        <div className="spinner-section">
          {renderSpinner()}
          
          <div className="loading-text-container">
            <h2 className="loading-title" style={{ color: currentColor.main }}>
              Loading{animated ? dots : '...'}
            </h2>
            
            {progress !== null && (
              <div className="progress-container">
                <div className="progress-bar" style={{ backgroundColor: currentColor.secondary }}>
                  <div 
                    className="progress-fill"
                    style={{
                      width: `${Math.min(100, Math.max(0, progress))}%`,
                      background: currentColor.gradient
                    }}
                  />
                </div>
                <span className="progress-text" style={{ color: currentColor.main }}>
                  {Math.round(progress)}% Complete
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoadingSpinner;
